import type { AdminCrossCurricularCatalog } from '../types/adminCrossCurricular';

const STORAGE_KEY = 'cp.adminCrossCurricularCatalog.v1';

let memoryCatalog: AdminCrossCurricularCatalog | null = null;
let inflight: Promise<AdminCrossCurricularCatalog> | null = null;

function cloneCatalog(catalog: AdminCrossCurricularCatalog): AdminCrossCurricularCatalog {
  return {
    topics: catalog.topics.map((t) => ({ ...t })),
    contents: catalog.contents.map((c) => ({ ...c })),
  };
}

function normalizeCatalog(raw: unknown): AdminCrossCurricularCatalog | null {
  if (!raw || typeof raw !== 'object') return null;
  const topics = (raw as { topics?: unknown }).topics;
  const contents = (raw as { contents?: unknown }).contents;
  if (!Array.isArray(topics) || !Array.isArray(contents)) return null;
  return {
    topics: topics
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row) => ({
        id: String(row.id ?? ''),
        name: String(row.name ?? '').trim(),
        sort_order: Number(row.sort_order) || 0,
        created_at: String(row.created_at ?? ''),
        updated_at: String(row.updated_at ?? ''),
      }))
      .filter((row) => row.id),
    contents: contents
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row) => ({
        id: String(row.id ?? ''),
        topic_id: String(row.topic_id ?? ''),
        body: String(row.body ?? '').trim(),
        sort_order: Number(row.sort_order) || 0,
        created_at: String(row.created_at ?? ''),
        updated_at: String(row.updated_at ?? ''),
      }))
      .filter((row) => row.id && row.topic_id),
  };
}

/** 세션 내 즉시 재사용(모달 반복 오픈). */
export function loadAdminCrossCurricularCatalogCache(): {
  found: boolean;
  catalog: AdminCrossCurricularCatalog;
} {
  if (memoryCatalog) {
    return { found: true, catalog: cloneCatalog(memoryCatalog) };
  }
  if (typeof window === 'undefined') {
    return { found: false, catalog: { topics: [], contents: [] } };
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { found: false, catalog: { topics: [], contents: [] } };
    const parsed = normalizeCatalog(JSON.parse(raw));
    if (!parsed) return { found: false, catalog: { topics: [], contents: [] } };
    memoryCatalog = parsed;
    return { found: true, catalog: cloneCatalog(parsed) };
  } catch {
    return { found: false, catalog: { topics: [], contents: [] } };
  }
}

export function saveAdminCrossCurricularCatalogCache(catalog: AdminCrossCurricularCatalog): void {
  const next = cloneCatalog(catalog);
  memoryCatalog = next;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...next, savedAt: Date.now() })
    );
  } catch {
    /* quota — 메모리 캐시는 유지 */
  }
}

export function invalidateAdminCrossCurricularCatalogCache(): void {
  memoryCatalog = null;
  inflight = null;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function getAdminCrossCurricularCatalogInflight(): Promise<AdminCrossCurricularCatalog> | null {
  return inflight;
}

export function setAdminCrossCurricularCatalogInflight(
  promise: Promise<AdminCrossCurricularCatalog> | null
): void {
  inflight = promise;
}
